// UX88 (docs/build-briefs/UX88-all-named-lists-alphabetical.md, DEC-339):
// the one name sort every list or picker of NAMED things uses. Pure and
// DOM-free so a node:test file can import it (admin.ts and main.ts cannot be
// imported without a DOM).
//
// Locale-aware, case-insensitive and accent-insensitive on the first pass
// (sensitivity "base"), with numbers compared as numbers so "Beer 2" sorts
// before "Beer 10". A tie on the first pass (only case or accents differ)
// falls back to a strict comparison so the order is deterministic.

const primaryCollator = new Intl.Collator("en", { sensitivity: "base", numeric: true });
const tieCollator = new Intl.Collator("en", { sensitivity: "variant", numeric: true });

export function compareNames(a: string, b: string): number {
  return primaryCollator.compare(a, b) || tieCollator.compare(a, b);
}

export interface SortByNameOptions<T> {
  /** Entries for which this returns true stay first, in their input order
   * (a deliberate default such as "None", "All" or "No section"); the rest
   * follow A to Z. */
  pinned?: (item: T) => boolean;
}

/** Returns a NEW array sorted by the shown label. Never mutates the input
 * and never touches any stored order. Array.prototype.sort is stable, so
 * items with identical labels keep their input order. */
export function sortByName<T>(
  items: readonly T[],
  label: (item: T) => string,
  options: SortByNameOptions<T> = {},
): T[] {
  const { pinned } = options;
  const head: T[] = [];
  const rest: T[] = [];
  for (const item of items) {
    if (pinned !== undefined && pinned(item)) {
      head.push(item);
    } else {
      rest.push(item);
    }
  }
  rest.sort((a, b) => compareNames(label(a), label(b)));
  return [...head, ...rest];
}

export interface NamedOption {
  value: string;
  label: string;
}

/** Options A to Z by shown label. */
export function sortOptions<T extends NamedOption>(
  options: readonly T[],
  pinned?: (option: T) => boolean,
): T[] {
  return sortByName(options, (option) => option.label, pinned === undefined ? {} : { pinned });
}

/** A vocabulary's ACTIVE entries as picker options, A to Z by label. When the
 * vocabulary has no active entries yet (the short window before the
 * vocabularies load) the fallback options are used, sorted the same way. */
export function activeVocabularyOptions(
  entries: readonly { value: string; label: string; active: boolean }[],
  fallback: readonly NamedOption[],
): NamedOption[] {
  const active = entries
    .filter((entry) => entry.active)
    .map((entry) => ({ value: entry.value, label: entry.label }));
  return sortOptions(active.length > 0 ? active : fallback);
}

/** Adds a value the row already carries (a retired category, a stored kind)
 * when the offered options lack it, then returns the whole list A to Z so the
 * merged-in value takes its alphabetical place. Never mutates the input. */
export function withStoredOption<T extends NamedOption>(
  offered: readonly T[],
  stored: T,
  pinned?: (option: T) => boolean,
): T[] {
  const merged = offered.some((option) => option.value === stored.value)
    ? [...offered]
    : [...offered, stored];
  return sortOptions(merged, pinned);
}
